import React from 'react';
import {
  Box, Button, Stack, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Typography, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import { get, put } from '../utils/api';
import PageLayout from '../components/common/PageLayout';

function groupPermissionsByArea(permissions) {
  const groups = {};
  (permissions || []).forEach(p => {
    const key = p.key || '';
    // Financial-data access is managed by the single finance:access umbrella (Orders + Temple + Kotilinga
    // + their sub-reports), so hide the granular Orders / Pledges / Reports checkboxes it now covers.
    if (key === 'reports:view') return;
    if (key.startsWith('orders:') || key.startsWith('pledges:')) return;
    if (key === 'event_reports:access' || key === 'sponsors_reports:access') return;
    const area = key.includes(':') ? key.split(':')[0] : 'other';
    if (!groups[area]) groups[area] = [];
    groups[area].push(p);
  });
  const order = ['admin', 'finance', 'calendar', 'orders', 'devotees', 'pledges', 'reports', 'communications', 'settings', 'users', 'meetings', 'other'];
  return order.filter(a => groups[a]?.length).map(area => ({ area, perms: groups[area] }));
}

export default function RolesPage() {
  const [roles, setRoles] = React.useState([]);
  const [allPermissions, setAllPermissions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ name: '', permissions: [] });
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesRes, permsRes] = await Promise.all([
        get('/api/admin/roles'),
        get('/api/admin/permissions'),
      ]);
      setRoles(rolesRes.roles || []);
      setAllPermissions(permsRes.permissions || []);
    } catch (e) {
      setError(e?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openEdit = (role) => {
    setEditing(role);
    setForm({
      name: role.name || '',
      permissions: Array.isArray(role.permissions) ? [...role.permissions] : [],
    });
    setModalOpen(true);
    setError('');
    setOk('');
  };

  const close = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const togglePermission = (key, checked) => {
    setForm(prev => ({
      ...prev,
      permissions: checked
        ? [...(prev.permissions || []), key]
        : (prev.permissions || []).filter(k => k !== key),
    }));
  };

  const onSave = async () => {
    setError('');
    setOk('');
    try {
      const isAdmin = String(form.name.trim() || editing.name).toLowerCase() === 'admin';
      // Keep admin:full off non-Admin roles. Preserve every other permission the role already has —
      // including hidden ones like reports:view — so editing a role never silently strips access.
      const perms = (form.permissions || []).filter(
        k => (k !== 'admin:full' || isAdmin)
      );
      await put(`/api/admin/roles/${editing.id}`, {
        name: form.name.trim() || editing.name,
        permissions: perms,
      });
      setOk('Role updated');
      setModalOpen(false);
      load();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const selectedPerms = new Set(form.permissions || []);
  // admin:full is only applicable to Admin role; hide for other roles
  const isAdminRole = String(form.name || editing?.name || '').toLowerCase() === 'admin';
  const permissionsForRole = (allPermissions || []).filter(
    p => p.key !== 'admin:full' || isAdminRole
  );
  const grouped = groupPermissionsByArea(permissionsForRole);

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Roles &amp; Permissions</Typography>
        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define default permissions for each role. When you assign a role to a user, their permission checkboxes will be pre-filled with these defaults (you can still override per user).
      </Typography>

      {error && <Box sx={{ mb: 2, color: 'error.main' }}>{error}</Box>}
      {ok && <Box sx={{ mb: 2, color: 'success.main' }}>{ok}</Box>}

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Default permissions</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.length === 0 && !loading && (
              <TableRow><TableCell colSpan={3}>No roles</TableCell></TableRow>
            )}
            {roles.map(r => (
              <TableRow key={r.id}>
                <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                <TableCell>
                  {(() => {
                    const visiblePermissions = Array.isArray(r.permissions)
                      ? r.permissions.filter(k => k !== 'reports:view')
                      : [];
                    return visiblePermissions.length > 0
                      ? visiblePermissions.join(', ')
                      : <Typography variant="body2" color="text.secondary">None</Typography>;
                  })()}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => openEdit(r)}>Edit permissions</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={modalOpen} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Edit role: {editing?.name}</DialogTitle>
        <DialogContent dividers>
          {error && <Box sx={{ mb: 2, color: 'error.main', fontSize: '0.875rem' }}>{error}</Box>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Role name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <Typography variant="subtitle2">Default permissions for this role</Typography>
            <FormGroup>
              {grouped.map(({ area, perms }) => (
                <Box key={area} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{area.replace(/_/g, ' ')}</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {perms.map(p => (
                      <FormControlLabel
                        key={p.id}
                        control={
                          <Checkbox
                            checked={selectedPerms.has(p.key)}
                            onChange={e => togglePermission(p.key, e.target.checked)}
                          />
                        }
                        label={p.key}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageLayout>
  );
}
